import { Component, Input, NgZone, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { ChartDateFormat, Task } from '@app/interfaces/lm/task';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { EChartsOption } from 'echarts';
import { ECharts } from 'echarts/core';

@Component({
    selector: 'app-task-chart',
    templateUrl: './task-chart.component.html',
    styleUrls: ['./task-chart.component.less'],
})
export class TaskChartComponent implements OnInit, OnChanges {
    @Input() data: Task[] = [];
    @Input() range!: ChartDateFormat;
    @Input() taskNameOnly = true;
    emptyArr: Array<Array<number | string>> = [];
    taskDataArr: Array<Array<number | string>> = [];
    options: EChartsOption = {
        grid: {
            containLabel: true,
            bottom: 40
        },
        xAxis: {
            type: 'value',
            interval: 1,
            max: () => {
                const { startTime, dueTime } = this.range;
                const diff = dayjs(dueTime).diff(dayjs(startTime), 'day');
                return diff;
            },
            axisLabel:
                {
                    rotate: 70,
                    formatter: (value: any, index: any) => {
                        const d = dayjs(this.range?.startTime);
                        return d.add(value, 'day').format('MMM DD');
                    },
                },
        },
        yAxis: {
            type: 'category',
            axisLine: { show: false },
            axisLabel: { show: true },
            axisTick: { show: false },

        },
        series: [],
    };
    markline: any;
    charts?: ECharts;

    constructor(private router: Router,
                private zone: NgZone) {
    }

    ngOnInit(): void {
    }

    async onChartInit(charts: ECharts): Promise<void> {
        this.charts = charts;
        if (this.charts) {
            this.charts.on('click', (params) => {
                if (params.seriesName === 'empty') {
                    return;
                }
                const data = this.data[params.dataIndex]; // projectId,id
                this.zone.run(() => {
                    this.router.navigate(['/project/task-dashboard'], {
                        queryParams: this.taskNameOnly ? {
                            projectId: data.projectId,
                            taskId: data.id,
                        } : {
                            projectId: data.projectId,
                            taskId: data.id,
                            from: 'my',
                        },
                    });
                });
            });
        }
    }

    getMarkLine(): void {
        const rangeStart = dayjs(this.range.startTime).subtract(1, 'day').format('YYYY-MM-DD');
        const rangeDue = dayjs(this.range.dueTime).add(1, 'day').format('YYYY-MM-DD');
        const today = dayjs(new Date());
        const isInRange = today.isBetween(rangeStart, rangeDue);
        const diffDay = isInRange ? today.diff(dayjs(this.range.startTime), 'day') : 0;
        this.markline = isInRange ? {
            symbol: ['none', 'none'],
            label: {
                show: false,
            },
            tooltip: {
                formatter: () => {
                    return 'Today';
                },
            },
            lineStyle: {
                type: 'dotted',
                width: 1.5,
                color: '#f89706',
                opacity: 0.5,
            },
            data: [
                {
                    xAxis: diffDay,
                },
            ],
        } : {};
    }

    ngOnChanges(changes: SimpleChanges): void {
        dayjs.extend(isBetween);
        dayjs.extend(isSameOrBefore);
        this.getMarkLine();
        this.emptyArr = [];
        this.taskDataArr = [];
        const { startTime, dueTime } = this.range;
        this.data.forEach((item) => {
            const itemDue = dayjs(item.dueTime).endOf('d');
            const itemStart = dayjs(item.startTime).startOf('d');
            const emptyDay = dayjs(itemStart).diff(dayjs(startTime), 'day');
            const emptyItem = [emptyDay < 0 ? 0 : emptyDay, this.taskNameOnly ? item.name : item.name + '(' + item.projectName + ')'];
            this.emptyArr = [...this.emptyArr, emptyItem];
            let notEmptyDay = 0;
            const rangeStart = dayjs(startTime);
            const rangeDue = dayjs(dueTime);
            if (dayjs(itemStart).isSameOrBefore(rangeStart) && rangeStart.isSameOrBefore(itemDue)) {
                    notEmptyDay = dayjs(itemDue).diff(rangeStart, 'day');
            }
            if (dayjs(itemStart).isSameOrBefore(rangeStart) && rangeDue.isSameOrBefore(itemDue)) {
                notEmptyDay = rangeDue.diff(rangeStart, 'day');
            }
            if (rangeStart.isSameOrBefore(itemStart) && dayjs(itemDue).isSameOrBefore(rangeDue)) {
                notEmptyDay = dayjs(itemDue).diff(dayjs(itemStart), 'day');
            }
            if (rangeStart.isSameOrBefore(itemStart) && rangeDue.isSameOrBefore(itemDue)) {
                notEmptyDay = rangeDue.diff(itemStart, 'day');
            }
            const notEmptyItem = [notEmptyDay < 0 ? 0 : notEmptyDay, this.taskNameOnly ? item.name : item.name + '(' + item.projectName + ')'];
            this.taskDataArr = [...this.taskDataArr, notEmptyItem];
        });
        // Update xAxis
        const diff = dayjs(dueTime).diff(dayjs(startTime), 'day');
        const dateInterval = Math.ceil(diff / 30);
        const updatedxAxis = {
            ...this.options.xAxis,
            interval: dateInterval ? dateInterval : 1,
        };
        //
        this.options = {
            ...this.options,
            dataZoom: [
                {
                    type: 'slider',
                    show: this.data.length > 10 ,
                    orient: 'vertical',
                    backgroundColor: 'rgb(201, 201, 202)',
                    fillerColor: 'rgb(162, 162, 163)',
                    borderColor: '#fff',
                    showDetail: false,
                    startValue: 0,
                    endValue: 9,
                    yAxisIndex: 0,
                    filterMode: 'empty',
                    width: 8,
                    height: '80%',
                    right: 0,
                    handleSize: 0,
                    zoomLock: true,
                    brushSelect: false,
                    top: 'middle',
                },
                {
                    type: 'inside',
                    show: this.data.length > 10,
                    yAxisIndex: 0,
                    brushSelect: false,
                    zoomOnMouseWheel: false,
                    moveOnMouseMove: true,
                    moveOnMouseWheel: true,
                },
            ],
            xAxis: {
                ...updatedxAxis,
            },
            tooltip: {
                trigger: 'item',
                borderColor: '#66666610',
                backgroundColor: '#ffffff',
                textStyle: {
                    fontFamily: 'monospace',
                    textShadowColor: '#66666670',
                    textShadowBlur: 4,
                    textShadowOffsetX: 1,
                    textShadowOffsetY: 2,
                },
            },
            series: [
                {
                    type: 'bar',
                    stack: 'total',
                    barMaxWidth: 25,
                    name: 'empty',
                    tooltip: {
                        show: false,
                    },
                    itemStyle: {
                        borderColor: 'transparent',
                        color: 'transparent',
                    },
                    emphasis: {
                        itemStyle: {
                            borderColor: 'transparent',
                            color: 'transparent',
                        },
                    },
                    data: this.emptyArr,
                    encode: {
                        // Map the "amount" column to X axis.
                        x: 'amount',
                        // Map the "product" column to Y axis
                        y: 'product',
                    },
                    markLine: this.markline,
                },
                {
                    type: 'bar',
                    stack: 'total',
                    name: 'Today',
                    // label: {
                    //     show: true,
                    //     formatter: '{b}',
                    //     position: 'inside'
                    // },
                    barMinHeight: 3,
                    itemStyle: {
                        borderRadius: [8, 8, 8, 8],
                        color: (param) => {
                            const randomColors = ['#ff758c', '#667eea', '#89f7fe', '#ff9a9e', '#fcb69f', '#ad5389', '#3f2b96', '#bc4e9c', '#3c1053', '#ff8c00', '#23074d', '#ff7eb3', '#764ba2', '#66a6ff', '#ff758c', '#667eea', '#89f7fe', '#ff9a9e', '#fcb69f', '#ad5389', '#3f2b96', '#bc4e9c', '#3c1053', '#ff8c00', '#23074d', '#ff7eb3', '#764ba2', '#66a6ff', '#ff758c', '#667eea', '#89f7fe', '#ff9a9e', '#fcb69f', '#ad5389', '#3f2b96', '#bc4e9c', '#3c1053', '#ff8c00', '#23074d', '#ff7eb3', '#764ba2', '#66a6ff'];
                            const colorIndex = param.dataIndex % randomColors.length;
                            return randomColors[colorIndex] || '#5470c6';
                        },
                    },
                    tooltip: {
                        formatter: (params: any) => {
                            const randomColors = ['#ff758c', '#667eea', '#89f7fe', '#ff9a9e', '#fcb69f', '#ad5389', '#3f2b96', '#bc4e9c', '#3c1053', '#ff8c00', '#23074d', '#ff7eb3', '#764ba2', '#66a6ff', '#ff758c', '#667eea', '#89f7fe', '#ff9a9e', '#fcb69f', '#ad5389', '#3f2b96', '#bc4e9c', '#3c1053', '#ff8c00', '#23074d', '#ff7eb3', '#764ba2', '#66a6ff', '#ff758c', '#667eea', '#89f7fe', '#ff9a9e', '#fcb69f', '#ad5389', '#3f2b96', '#bc4e9c', '#3c1053', '#ff8c00', '#23074d', '#ff7eb3', '#764ba2', '#66a6ff'];
                            const colorIndex = params.dataIndex % randomColors.length;
                            const dotColor = randomColors[colorIndex] || '#5470c6';
                            const taskDot = '<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:' + dotColor + '"></span>';
                            const projectDot = '<span style="margin-left:17px"></span>';
                            const startDot = '<span style="margin-left:17px"></span>';
                            const endDot = '<span style="margin-left:17px"></span>';
                            const data = this.data[params.dataIndex];
                            let tip = '';
                            tip += taskDot + '<span style="font-weight:700;margin-right:3px">Task:</span>' + data?.name + '</br>' + projectDot + '<span style="font-weight:700;margin-right:3px">Project:</span>' + data?.projectName + '</br>' +
                                startDot + '<span style="font-weight:700;margin-right:3px">Start:</span>' + dayjs(data?.startTime).format('YYYY-MM-DD') + '</br>' + endDot + '<span style="font-weight:700;margin-right:3px">End:</span>' + dayjs(data?.dueTime).format('YYYY-MM-DD');
                            return tip;
                        },
                    },
                    markLine: this.markline,
                    data: this.taskDataArr,
                },
            ],
        };
    }

}
